// The solution-config FIELD MODEL for every reconciliation surface (the cockpit
// list, the peek, the config form, the Workbench stories) — keyed by the WIRE
// field names (`@ems-app/schemas/sites/discoveries`' vocabulary), so the same
// keys flow from the observation endpoint through suggestions to the cells with
// no mapping layer. Pure module — no React.

import type { TFunction } from 'i18next';
import {
  OBSERVABLE_FIELD_KIND,
  type DiscoverableConfigField,
} from '@ems-app/schemas/sites/discoveries';
import type { SiteListItem, SiteSuggestion, SolutionConfig, SolutionSummary } from '@ems-app/schemas/sites/responses';
import { isOperationalSite, type SolutionConfigValues } from '@ems-app/schemas/sites/requests';
import {
  OBSERVATION_FAMILIES,
  byImpact,
  type ObservationFamily,
  type ObservationImpact,
  type ObservationKind,
} from '@ems-app/schemas/observations/registry';

export type ConfigSection =
  | 'grid'
  | 'solar'
  | 'generator'
  | 'providerGenerator'
  | 'battery'
  | 'wind'
  | 'cooling'
  | 'system';

export interface ConfigFieldMeta {
  /** sites-namespace i18n key for the field label. */
  labelKey: string;
  section: ConfigSection;
  /** Display unit suffix (SI symbols stay Latin — ADR-030). */
  unit?: string;
}

export const CONFIG_FIELD_META: Record<DiscoverableConfigField, ConfigFieldMeta> = {
  has_grid: { labelKey: 'filters.grid', section: 'grid' },
  grid_provider: { labelKey: 'filters.provider', section: 'grid' },
  grid_capacity_kw: { labelKey: 'config.fields.gridCapacity', section: 'grid', unit: 'kW' },
  has_solar: { labelKey: 'filters.solar', section: 'solar' },
  solar_capacity_kwp: { labelKey: 'config.fields.solarCapacity', section: 'solar', unit: 'kWp' },
  has_generator: { labelKey: 'filters.generator', section: 'generator' },
  generator_capacity_kva: { labelKey: 'config.fields.generatorCapacity', section: 'generator', unit: 'kVA' },
  generator_units: { labelKey: 'config.fields.generatorUnits', section: 'generator' },
  generator_tank_l: { labelKey: 'config.fields.generatorTank', section: 'generator', unit: 'L' },
  has_provider_generator: { labelKey: 'config.sections.providerGenerator', section: 'providerGenerator' },
  provider_generator_name: { labelKey: 'config.fields.providerGeneratorName', section: 'providerGenerator' },
  provider_generator_auto_failover: { labelKey: 'config.fields.providerGeneratorAutoFailover', section: 'providerGenerator' },
  has_battery: { labelKey: 'detail.fields.battery', section: 'battery' },
  battery_chemistry: { labelKey: 'config.fields.batteryChemistry', section: 'battery' },
  battery_capacity_ah: { labelKey: 'config.fields.batteryCapacity', section: 'battery', unit: 'Ah' },
  has_wind: { labelKey: 'detail.fields.wind', section: 'wind' },
  wind_capacity_kw: { labelKey: 'config.fields.windCapacity', section: 'wind', unit: 'kW' },
  has_cooling: { labelKey: 'detail.fields.cooling', section: 'cooling' },
  cooling_units: { labelKey: 'config.fields.coolingUnits', section: 'cooling' },
  cooling_capacity_kw: { labelKey: 'config.fields.coolingCapacity', section: 'cooling', unit: 'kW' },
  power_system_capacity_kw: { labelKey: 'config.fields.powerSystemCapacity', section: 'system', unit: 'kW' },
};

/** Section → sites-namespace label key (form section titles + tooltips). */
export const SECTION_LABEL_KEY: Record<ConfigSection, string> = {
  grid: 'filters.grid',
  solar: 'filters.solar',
  generator: 'filters.generator',
  providerGenerator: 'config.sections.providerGenerator',
  battery: 'detail.fields.battery',
  wind: 'detail.fields.wind',
  cooling: 'detail.fields.cooling',
  system: 'config.sections.system',
};

/** The form's section walk: presence flag (null = always-on section) + its value fields. */
export const FORM_SECTIONS: { section: ConfigSection; flag: DiscoverableConfigField | null; fields: DiscoverableConfigField[] }[] = [
  { section: 'grid', flag: 'has_grid', fields: ['grid_provider', 'grid_capacity_kw'] },
  { section: 'solar', flag: 'has_solar', fields: ['solar_capacity_kwp'] },
  { section: 'generator', flag: 'has_generator', fields: ['generator_capacity_kva', 'generator_units', 'generator_tank_l'] },
  { section: 'providerGenerator', flag: 'has_provider_generator', fields: ['provider_generator_name', 'provider_generator_auto_failover'] },
  { section: 'battery', flag: 'has_battery', fields: ['battery_chemistry', 'battery_capacity_ah'] },
  { section: 'wind', flag: 'has_wind', fields: ['wind_capacity_kw'] },
  { section: 'cooling', flag: 'has_cooling', fields: ['cooling_units', 'cooling_capacity_kw'] },
  { section: 'system', flag: null, fields: ['power_system_capacity_kw'] },
];

/** The value slice every reconcile surface passes around — a period's fields
 *  without the timeline columns (the form's shape; SolutionConfig extends it). */
export type ConfigValues = SolutionConfigValues;

/** A period/summary/values object → its config-values slice keyed by wire names. */
export function toConfigValues(c: Partial<SolutionConfig>): ConfigValues {
  return {
    has_grid: c.has_grid ?? false,
    grid_provider: c.grid_provider ?? null,
    grid_capacity_kw: c.grid_capacity_kw ?? null,
    has_solar: c.has_solar ?? false,
    solar_capacity_kwp: c.solar_capacity_kwp ?? null,
    has_generator: c.has_generator ?? false,
    generator_capacity_kva: c.generator_capacity_kva ?? null,
    generator_units: c.generator_units ?? null,
    generator_tank_l: c.generator_tank_l ?? null,
    has_provider_generator: c.has_provider_generator ?? false,
    provider_generator_name: c.provider_generator_name ?? null,
    provider_generator_auto_failover: c.provider_generator_auto_failover ?? false,
    has_battery: c.has_battery ?? false,
    battery_chemistry: c.battery_chemistry ?? null,
    battery_capacity_ah: c.battery_capacity_ah ?? null,
    has_wind: c.has_wind ?? false,
    wind_capacity_kw: c.wind_capacity_kw ?? null,
    has_cooling: c.has_cooling ?? false,
    cooling_units: c.cooling_units ?? null,
    cooling_capacity_kw: c.cooling_capacity_kw ?? null,
    power_system_capacity_kw: c.power_system_capacity_kw ?? null,
  };
}

/** The values of a card switched OFF are not sent (the form keeps what was typed under a card until Save, so a card
 *  flipped back on finds its values; a save carries only what stands): every section whose flag is off has its
 *  value fields nulled — the flags themselves stay. Both hosts' commits run it (the wizard's, the drawer's). */
export function stripOffSections(values: ConfigValues): ConfigValues {
  const out: Record<string, unknown> = { ...values };
  for (const { flag, fields } of FORM_SECTIONS) {
    if (flag === null || out[flag] === true) continue;
    for (const f of fields) out[f] = OBSERVABLE_FIELD_KIND[f] === 'boolean' ? false : null;
  }
  return out as ConfigValues;
}

/** Blank required fields of the PRESENT sources — completeness is binary (every
 *  present-source field required; the provider generator requires its NAME only
 *  — its capacity is the provider's asset) + the rated system capacity.
 *  Tracked as Missing, never a write gate (ADR-056 §4). */
export function requiredGaps(c: ConfigValues): DiscoverableConfigField[] {
  const gaps: DiscoverableConfigField[] = [];
  const blank = (v: number | string | null | undefined) => v === null || v === undefined || v === '';
  if (c.has_grid) {
    if (blank(c.grid_provider)) gaps.push('grid_provider');
    if (blank(c.grid_capacity_kw)) gaps.push('grid_capacity_kw');
  }
  if (c.has_provider_generator && blank(c.provider_generator_name)) gaps.push('provider_generator_name');
  if (c.has_solar && blank(c.solar_capacity_kwp)) gaps.push('solar_capacity_kwp');
  if (c.has_generator) {
    if (blank(c.generator_capacity_kva)) gaps.push('generator_capacity_kva');
    if (blank(c.generator_units)) gaps.push('generator_units');
    if (blank(c.generator_tank_l)) gaps.push('generator_tank_l');
  }
  if (c.has_battery) {
    if (blank(c.battery_chemistry)) gaps.push('battery_chemistry');
    if (blank(c.battery_capacity_ah)) gaps.push('battery_capacity_ah');
  }
  if (c.has_wind && blank(c.wind_capacity_kw)) gaps.push('wind_capacity_kw');
  if (c.has_cooling) {
    if (blank(c.cooling_units)) gaps.push('cooling_units');
    if (blank(c.cooling_capacity_kw)) gaps.push('cooling_capacity_kw');
  }
  if (blank(c.power_system_capacity_kw)) gaps.push('power_system_capacity_kw');
  return gaps;
}

/** Distinct fields to touch — open-suggestion fields ∪ required gaps (the chip's
 *  one-line "{n} fields" secondary, ADR-056 §6). */
export function fieldsToTouch(suggestions: SiteSuggestion[], config: ConfigValues | null): number {
  const fields = new Set<string>(config ? requiredGaps(config) : []);
  for (const s of suggestions) if (s.status === 'open') fields.add(s.field);
  return fields.size;
}

/** A suggestion's discovered value (canonical text) rendered for humans — typed
 *  per field kind; the consumer localizes numbers itself when needed. */
export function discoveredDisplay(field: DiscoverableConfigField, value: string | null, t: TFunction): string {
  if (value === null) return t('reconcile.observedAbsent');
  switch (OBSERVABLE_FIELD_KIND[field]) {
    case 'boolean':
      return value === 'true' ? t(SECTION_LABEL_KEY[CONFIG_FIELD_META[field].section]) : t('reconcile.observedAbsent');
    case 'integer':
    case 'decimal': {
      const unit = CONFIG_FIELD_META[field].unit;
      return unit ? `${value} ${unit}` : value;
    }
    default:
      return value;
  }
}

/** The declared value of a field rendered for humans (the suggestion rows'
 *  "Declared:" side + cell tooltips). */
export function declaredDisplay(field: DiscoverableConfigField, config: ConfigValues | null, t: TFunction): string {
  const v = config?.[field as keyof ConfigValues];
  if (v === null || v === undefined || v === '') return '—';
  if (typeof v === 'boolean') {
    return v ? t(SECTION_LABEL_KEY[CONFIG_FIELD_META[field].section]) : t('reconcile.observedAbsent');
  }
  const unit = CONFIG_FIELD_META[field].unit;
  return unit ? `${v} ${unit}` : String(v);
}

/** A list summary → the config-values slice (presence derives from `sources`). */
export function summaryToConfigValues(s: SolutionSummary): ConfigValues {
  return toConfigValues({
    ...s,
    has_grid: s.sources.includes('grid'),
    has_solar: s.sources.includes('solar'),
    has_generator: s.sources.includes('generator'),
    has_provider_generator: s.sources.includes('provider_generator'),
    has_battery: s.sources.includes('battery'),
    has_wind: s.sources.includes('wind'),
  });
}

/** The health-cell inputs from a list item (the shared SiteConfigHealth shape). */
// ── THE SITES COMPARATOR (ADR-061 §4) ───────────────────────────────────────
// Vocabulary is shared, comparison is NOT: this function decides which families
// a site row raises, and it lives here, in the module that owns the data. It
// calls no other module's comparator, and nothing outside sites calls it.
//
// It replaces `healthOfItem`'s private review / incomplete / ok states, which
// were a conflict and a missing wearing other names, fused into ONE bucket per
// row — so a site with three blanks AND a drift reported only the drift.
//
// Who raises observations (architect-ruled, mirrored exactly in the SQL): ACTIVE
// and SUSPENDED. A suspended site is still yours and comes back, so wrong data is
// still real work — and silence-by-suspending would be a way to hide the queue.
// DRAFT and DECOMMISSIONED raise nothing: onboarding owns its own gaps (the status
// cell already counts them), and a dead site has nothing to fix.
export interface SiteObservation {
  family: ObservationFamily;
  kind: ObservationKind;
  impact: ObservationImpact;
  /** Which config field it is about — absent on `site.no_config`, which is
   *  about the whole site. Rendered through CONFIG_FIELD_META's i18n key. */
  field?: DiscoverableConfigField;
  /** Conflict only — carried for the peek's side-by-side. */
  suggestion?: SiteSuggestion;
}

function observe(family: ObservationFamily, extra: Partial<SiteObservation> = {}): SiteObservation {
  const def = OBSERVATION_FAMILIES[family];
  return { family, kind: def.kind, impact: def.impact, ...extra };
}

export function siteObservations(item: SiteListItem, face: 'registry' | 'solution' = 'solution'): SiteObservation[] {
  // Only the OPERATIONAL family raises observations (sites/requests §status families).
  if (!isOperationalSite(item.status)) return [];
  // The REGISTRY face reads flags the list payload carries; the site's own
  // record gaps are computed server-side (the DDL forbids most of them) and
  // arrive as `registry_gaps`, so the client never re-derives a rule the SQL
  // owns — the two could not disagree even if someone tried.
  if (face === 'registry') return (item.registry_gaps ?? []).map((family) => observe(family as ObservationFamily)).sort(byImpact);
  return solutionPeekObservations(item.status, item.solution ? summaryToConfigValues(item.solution) : null, item.suggestions);
}

/** The SAME solution-face derivation from the peek's inputs (detail payload) —
 *  one rule, two entry shapes, so a row's mark and the peek's card list cannot
 *  disagree. Live findings only; the ignored ones are the caller's foot cards. */
export function solutionPeekObservations(
  status: SiteListItem['status'],
  config: ConfigValues | null,
  suggestions: SiteSuggestion[],
): SiteObservation[] {
  if (!isOperationalSite(status)) return [];
  // No config at all is the register's ONE high: the site cannot go live.
  if (!config) return [observe('site.no_config')];
  const out: SiteObservation[] = [
    ...requiredGaps(config).map((field) => observe('site.config_field_blank', { field })),
    ...suggestions
      .filter((s) => s.status === 'open')
      .map((s) => observe('site.discovered_differs', { field: s.field as DiscoverableConfigField, suggestion: s })),
  ];
  // Worst first — the row renders its most severe and counts the rest.
  return out.sort(byImpact);
}

/** The row's lifecycle facts, which are NOT observations — the identity cell and
 *  the status chip still need them. */
export function statusDateOf(item: SiteListItem): string | null {
  return item.status === 'suspended' ? item.suspended_on : item.status === 'decommissioned' ? item.decommissioned_on : null;
}
